import { DashboardLayoutType } from "api/model/dashboard/DashboardComponent";
import {
  DashboardComponentAlign,
  DashboardComponentGroup,
  DashboardComponentJustify,
} from "api/model/dashboard/DashboardComponentGroup";
import { LayoutType } from "api/model/dashboard/DashboardReportOptions";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import React, { useState } from "react";
import { T } from "services/lang/LanguageService";
import useDashboardStore from "store/dashboard-store";
import { ComponentTheme } from "utils/enums";
import { tabUpdateInDashboard, updateDashboardTabs } from "../../utils";
import { componentThemeOptions } from "../group-component/ComponentThemeEditor";
import { getDefaultLayout } from "../layouts/helpers";
import { ReorderType } from "./reorder/DraggableItem";
import { ComponentReorderConfig } from "./reorder/Reorder";

interface GroupConfiguratorProps {
  tabId: number;
  group: DashboardComponentGroup;
  isDialogVisible: boolean;
  isReportMode?: boolean;
  setIsDialogVisible: (isDialogVisible: boolean) => void;
  setIsComponentsReorderEnabled: (componentReorderConfig: ComponentReorderConfig) => void;
  setMoveTabGroupDialogVisibility: (moveTabGroupDialogVisibility: boolean) => void;
  setDashboardComponentCreationDialogVisibility: (
    dashboardComponentCreationDialogVisibility: boolean
  ) => void;
  setGroupHeaderComponentCreationDialogVisibility: (
    groupHeaderComponentCreationDialogVisibility: boolean
  ) => void;
}

const defaultGroupReportOptions = {
  avoidPageBreak: false,
  layoutType: "VERTICAL" as LayoutType,
  theme: ComponentTheme.LIGHT,
  fullWidth: false,
};

const layoutOptions = [
  { label: T("layout.vertical"), value: DashboardLayoutType.VERTICAL },
  { label: T("layout.horizontal-flex"), value: DashboardLayoutType.HORIZONTAL_FLEX },
  { label: T("layout.grid"), value: DashboardLayoutType.GRID },
];

const justifyOptions = [
  { label: T("layout.start"), value: DashboardComponentJustify.START },
  { label: T("layout.center"), value: DashboardComponentJustify.CENTER },
  { label: T("layout.end"), value: DashboardComponentJustify.END },
  { label: T("layout.space-between"), value: DashboardComponentJustify.BETWEEN },
  { label: T("layout.space-around"), value: DashboardComponentJustify.AROUND },
  { label: T("layout.space-evenly"), value: DashboardComponentJustify.EVENLY },
];

const alignOptions = [
  { label: T("layout.start"), value: DashboardComponentAlign.START },
  { label: T("layout.center"), value: DashboardComponentAlign.CENTER },
  { label: T("layout.end"), value: DashboardComponentAlign.END },
  { label: T("layout.stretch"), value: DashboardComponentAlign.STRETCH },
];

const GroupConfigurator = ({
  tabId,
  group,
  isDialogVisible,
  isReportMode,
  setIsDialogVisible,
  setIsComponentsReorderEnabled,
  setMoveTabGroupDialogVisibility,
  setDashboardComponentCreationDialogVisibility,
  setGroupHeaderComponentCreationDialogVisibility,
}: GroupConfiguratorProps) => {
  const dashboard = useDashboardStore(state => state.dashboard);
  const [localGroup, setLocalGroup] = useState<DashboardComponentGroup>({ ...group });
  const [newGroupTitle, setNewGroupTitle] = useState<string>(group.title || "");
  const setIsGroupReorderEnabled = useDashboardStore(state => state.setIsGroupReorderEnabled);
  const [tabIndex, setTabIndex] = useState(0);

  const handleReorderComponents = (isGroupHeader?: boolean) => {
    setIsGroupReorderEnabled(false);
    setIsComponentsReorderEnabled({
      isVisible: true,
      type: isGroupHeader ? ReorderType.GROUP_HEADER_COMPONENTS : ReorderType.COMPONENTS,
    });
  };

  if (!dashboard) {
    return <ProgressSpinner />;
  }

  const handleInputChange = (
    field: string,
    value: DashboardComponentAlign | DashboardComponentJustify | DashboardLayoutType
  ) => {
    setLocalGroup(prevGroup => ({
      ...prevGroup,
      layout: {
        ...prevGroup.layout,
        [field]: value,
      },
    }));
  };

  const handleReportOptionsInputChange = (
    field: "theme" | "fullWidth" | "avoidPageBreak",
    value: ComponentTheme | boolean | undefined
  ) => {
    const updatedReportOptions = {
      ...defaultGroupReportOptions,
      ...localGroup.reportOptions,
      [field]: value,
    };
    setLocalGroup(prevGroup => ({
      ...prevGroup,
      reportOptions: updatedReportOptions,
    }));
  };

  const handleSave = () => {
    updateDashboardTabs(
      dashboard,
      dashboard.dashboardTabsJs?.map(tab => {
        return {
          ...tab,
          componentGroups: tab.componentGroups.map(g => {
            if (g.id === localGroup.id) {
              const layoutTypeChanged = g.layout.type !== localGroup.layout.type;
              const updatedComponents = g.components?.map(component => {
                if (layoutTypeChanged) {
                  return {
                    ...component,
                    layout: getDefaultLayout(localGroup.layout.type),
                  };
                }
                return component;
              });
              return {
                ...g,
                title: newGroupTitle,
                components: updatedComponents,
                layout: localGroup.layout,
                reportOptions: localGroup.reportOptions,
                collapsable: localGroup.collapsable,
                isCollapsed: localGroup.isCollapsed,
              };
            }
            return g;
          }),
        };
      })
    );
    setIsDialogVisible(false);
  };

  const groupRemoveConfirm = (id: string) => {
    setIsDialogVisible(false);
    easasConfirmDialog({
      icon: "pi pi-exclamation-triangle",
      header: T("confirm-dialog.remove-group"),
      message: T("confirm-dialog.are-you-sure-you-want-to-delete-this-group"),
      confirmedCallback: () => {
        handleRemoveGroup(id);
      },
    });
  };

  const handleRemoveGroup = (id: string) => {
    if (!dashboard) return;

    const currentTabIndex = tabId;
    const updatedComponentGroups = dashboard.dashboardTabsJs[currentTabIndex].componentGroups.filter(
      (groupToFilter: DashboardComponentGroup) => groupToFilter.id !== id
    );

    const updatedDashboardTabs = tabUpdateInDashboard(dashboard.dashboardTabsJs, currentTabIndex, {
      componentGroups: updatedComponentGroups,
    });

    updateDashboardTabs(dashboard, updatedDashboardTabs);
  };

  const handleMoveGroup = () => {
    setMoveTabGroupDialogVisibility(true);
    setIsDialogVisible(false);
  };

  const handleCollapsableChange = (e: InputSwitchChangeEvent) => {
    if (!e.value) {
      setLocalGroup(prevGroup => ({
        ...prevGroup,
        collapsable: e.value,
        isCollapsed: e.value,
      }));
    }
    setLocalGroup(prevGroup => ({
      ...prevGroup,
      collapsable: e.value,
    }));
  };

  const handleConfiguratorFooter = () => {
    return (
      <>
        <Button
          tooltip={T("word.cancel")}
          label={T("word.cancel")}
          icon="pi pi-times"
          className="component-editor-button icon-red"
          onClick={() => setIsDialogVisible(false)}
        />
        <Button
          tooltip={T("action.save")}
          label={T("action.save")}
          icon="pi pi-check"
          className="component-editor-button icon-green"
          onClick={handleSave}
        />
      </>
    );
  };

  const renderInputField = (
    id: string,
    label: string,
    value: any,
    options: any[] | undefined,
    onChange: (e: any) => void,
    inputType: "dropdown" | "inputnumber",
    disabled: boolean
  ) => {
    return (
      <div className="w-full gap-2 flex flex-column align-items-left">
        <label htmlFor={id}>{label}</label>
        {inputType === "dropdown" ? (
          <Dropdown
            id={id}
            className="w-full"
            value={value}
            options={options}
            onChange={onChange}
            disabled={disabled}
          />
        ) : (
          <InputNumber id={id} className="w-full" value={value} onChange={onChange} disabled={disabled} />
        )}
      </div>
    );
  };

  return (
    <Dialog
      header={T("label.group-configurator")}
      visible={isDialogVisible}
      onHide={() => setIsDialogVisible(false)}
      draggable={false}
      blockScroll
      style={{ width: "500px" }}
      footer={handleConfiguratorFooter}
    >
      <div className="flex flex-column gap-4">
        <div className="w-full gap-2 flex flex-column">
          <div>{T("label.group-title")}</div>
          <InputText
            value={newGroupTitle}
            className="w-full"
            onChange={e => setNewGroupTitle(e.target.value)}
          />
        </div>
        <div className="flex flex-row justify-content-between align-items-end">
          <div className="flex flex-row gap-2">
            <div className="flex flex-column gap-2">
              <label htmlFor="height">{T("label.collapsable")}</label>
              <InputSwitch
                inputId="collapsable"
                checked={localGroup?.collapsable ?? false}
                onChange={handleCollapsableChange}
              />
            </div>
            {localGroup?.collapsable && (
              <div className="flex flex-column gap-2">
                <label htmlFor="height">{T("label.collapsed")}</label>
                <InputSwitch
                  inputId="collapsed"
                  checked={localGroup?.isCollapsed ?? false}
                  onChange={e =>
                    setLocalGroup((prevState: DashboardComponentGroup) => ({
                      ...prevState,
                      isCollapsed: e.value,
                    }))
                  }
                />
              </div>
            )}
          </div>
          <div className="flex flex-row gap-2">
            <Button
              icon="pi pi-arrow-right"
              label={T("action.move")}
              tooltip={T("action.move-group")}
              className="component-editor-button"
              onClick={handleMoveGroup}
              tooltipOptions={{ position: "bottom" }}
            />
            <Button
              icon="pi pi-trash"
              label={T("action.remove")}
              tooltip={T("confirm-dialog.remove-group")}
              className="component-editor-button icon-red"
              onClick={() => groupRemoveConfirm(group.id)}
              tooltipOptions={{ position: "bottom" }}
            />
          </div>
        </div>
        <TabView activeIndex={tabIndex} onTabChange={e => setTabIndex(e.index)}>
          <TabPanel header={T("label.group-content")}>
            <div className="flex flex-column gap-4">
              <div className="w-full flex flex-row gap-3">
                <Button
                  icon="pi pi-plus"
                  label={T("action.add")}
                  tooltip={T("action.add-component")}
                  className="component-editor-button icon-green"
                  onClick={() => setDashboardComponentCreationDialogVisibility(true)}
                  tooltipOptions={{ position: "bottom" }}
                />
                <Button
                  icon="pi pi-arrow-right-arrow-left"
                  label={T("action.reorder")}
                  tooltip={T("action.reorder-components")}
                  className="component-editor-button"
                  onClick={() => handleReorderComponents()}
                  tooltipOptions={{ position: "bottom" }}
                />
              </div>
              <div className="w-full">
                {renderInputField(
                  "type",
                  T("label.layout-type"),
                  localGroup.layout.type,
                  layoutOptions,
                  e => handleInputChange("type", e.value),
                  "dropdown",
                  false
                )}
              </div>
              {localGroup.layout.type !== DashboardLayoutType.GRID && (
                <div className="flex flex-row gap-4 justify-content-between">
                  <div className="flex flex-column gap-4">
                    {renderInputField(
                      "align",
                      T("label.vertical-align"),
                      localGroup.layout.align,
                      alignOptions,
                      e => handleInputChange("align", e.value),
                      "dropdown",
                      false
                    )}
                  </div>
                  <div className="flex flex-column gap-4">
                    {renderInputField(
                      "justify",
                      T("label.horizontal-align"),
                      localGroup.layout.justify,
                      justifyOptions,
                      e => handleInputChange("justify", e.value),
                      "dropdown",
                      localGroup.layout.type === DashboardLayoutType.VERTICAL
                    )}
                  </div>
                </div>
              )}
              {isReportMode && (
                <div className="flex flex-row justify-content-between">
                  <div className="flex flex-column gap-2">
                    <label htmlFor="theme">{T("word.theme")}</label>
                    <Dropdown
                      id="theme"
                      value={localGroup.reportOptions?.theme?.toLowerCase()}
                      options={componentThemeOptions}
                      onChange={e =>
                        handleReportOptionsInputChange("theme", e.value.toUpperCase() as ComponentTheme)
                      }
                    />
                  </div>
                  <div className="flex flex-column gap-2">
                    <label htmlFor="avoidPageBreak">{T("action.avoid-page-break")}</label>
                    <InputSwitch
                      id="avoidPageBreak"
                      checked={Boolean(localGroup.reportOptions?.avoidPageBreak)}
                      onChange={e => handleReportOptionsInputChange("avoidPageBreak", e.value)}
                    />
                  </div>
                  <div className="flex flex-column gap-2">
                    <label htmlFor="fullWidth">{T("action.use-full-width")}</label>
                    <InputSwitch
                      id="fullWidth"
                      checked={Boolean(localGroup.reportOptions?.fullWidth)}
                      onChange={e => handleReportOptionsInputChange("fullWidth", e.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          </TabPanel>
          <TabPanel header={T("label.group-header-content")}>
            <div className="w-full flex flex-row gap-3">
              <Button
                icon="pi pi-plus"
                label={T("action.add")}
                tooltip={T("action.add-component-to-group-header")}
                className="component-editor-button icon-green h-full"
                onClick={() => setGroupHeaderComponentCreationDialogVisibility(true)}
                tooltipOptions={{ position: "bottom" }}
              />
              <Button
                icon="pi pi-arrow-right-arrow-left"
                label={T("action.reorder")}
                tooltip={T("action.reorder-components")}
                className="component-editor-button"
                onClick={() => handleReorderComponents(true)}
                tooltipOptions={{ position: "bottom" }}
              />
            </div>
          </TabPanel>
        </TabView>
      </div>
    </Dialog>
  );
};

export default GroupConfigurator;
